import { CAMPAIGN_STATUS_CONFIG, DELIVERY_STATUS_CONFIG } from '../types/campaign';
import { INVOICE_STATUS_BADGE_COLORS, getInvoiceStatusBadgeColor } from './invoiceStatusColors';

export type StatusBadgeVariant = 'solid' | 'soft';

// Legacy PT status strings still stored on older rows — normalized before lookup
const STATUS_ALIASES: Record<string, string> = {
  Consulta: 'Consultation',
  Confirmada: 'Confirmed',
  Activa: 'In Progress',
  'Produção': 'Production',
  'Faturação': 'Billing',
  'Concluída': 'Completed',
  Cancelada: 'Cancelled',
};

const CAMPAIGN_SOFT: Record<string, string> = {
  Consultation: 'bg-slate-100 text-slate-600',
  Confirmed: 'bg-sky-50 text-sky-700',
  'In Progress': 'bg-blue-50 text-blue-700',
  Billing: 'bg-amber-50 text-amber-700',
  Completed: 'bg-emerald-50 text-emerald-700',
};

const DELIVERABLE_SOFT: Record<string, string> = {
  Draft: 'bg-slate-100 text-slate-600',
  Production: 'bg-blue-50 text-blue-700',
  Delivered: 'bg-sky-50 text-sky-700',
  Approved: 'bg-teal-50 text-teal-700',
  Rejected: 'bg-rose-50 text-rose-700',
  Published: 'bg-emerald-50 text-emerald-700',
};

const FALLBACK_SOFT = 'bg-slate-100 text-slate-600';
const FALLBACK_SOLID = 'bg-gray-100 text-gray-800';

function normalize(status: string): string {
  return STATUS_ALIASES[status] ?? status;
}

function solidClass(config: { bgColor: string; textColor: string } | undefined): string | undefined {
  return config ? `${config.bgColor} ${config.textColor}` : undefined;
}

export function getCampaignStatusBadgeClass(status: string, variant: StatusBadgeVariant = 'solid'): string {
  const key = normalize(status);
  if (variant === 'soft') {
    return CAMPAIGN_SOFT[key] ?? FALLBACK_SOFT;
  }
  return solidClass(CAMPAIGN_STATUS_CONFIG[key as keyof typeof CAMPAIGN_STATUS_CONFIG]) ?? FALLBACK_SOLID;
}

export function getDeliverableStatusBadgeClass(status: string, variant: StatusBadgeVariant = 'solid'): string {
  const key = normalize(status);
  if (variant === 'soft') {
    return DELIVERABLE_SOFT[key] ?? FALLBACK_SOFT;
  }
  return solidClass(DELIVERY_STATUS_CONFIG[key as keyof typeof DELIVERY_STATUS_CONFIG]) ?? FALLBACK_SOLID;
}

/**
 * Combined lookup for tables that render campaign, deliverable, invoice and
 * payment statuses through a single badge helper. Invoice/payment statuses
 * always use the shared invoice palette regardless of variant.
 */
export function getStatusBadgeClass(status: string, variant: StatusBadgeVariant = 'soft'): string {
  const key = normalize(status);
  if (variant === 'soft' ? CAMPAIGN_SOFT[key] : CAMPAIGN_STATUS_CONFIG[key as keyof typeof CAMPAIGN_STATUS_CONFIG]) {
    return getCampaignStatusBadgeClass(key, variant);
  }
  if (variant === 'soft' ? DELIVERABLE_SOFT[key] : DELIVERY_STATUS_CONFIG[key as keyof typeof DELIVERY_STATUS_CONFIG]) {
    return getDeliverableStatusBadgeClass(key, variant);
  }
  if (INVOICE_STATUS_BADGE_COLORS[key]) {
    return getInvoiceStatusBadgeColor(key);
  }
  return variant === 'soft' ? FALLBACK_SOFT : FALLBACK_SOLID;
}
